import { useState } from "react";
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  SlidersHorizontal,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { EMPTY_SIGNING_FILTER, type SigningFilterValue } from "../status";
import type { SigningItemStatus } from "../types";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

type Dimension = "status" | "signer" | "date";

const DIMENSIONS: Dimension[] = ["status", "signer", "date"];

const DIMENSION_ICON: Record<Dimension, LucideIcon> = {
  status: CircleDot,
  signer: Users,
  date: Calendar,
};

const STATUSES: SigningItemStatus[] = [
  "awaiting_you",
  "awaiting_others",
  "finalizing",
  "signed",
  "failed",
  "cancelled",
  "expired",
  // sessionItemStatus's fallback for an unrecognised backend status — must be
  // filterable or those rows become unreachable once any status is selected.
  "unknown",
];

interface SigningFilterControlProps {
  /** The people present in the current list — derived from the rows. */
  signers: Array<{ id: number; name: string }>;
  value: SigningFilterValue;
  onChange: (next: SigningFilterValue) => void;
}

/**
 * Cascading filter for the Signing list — the same box-then-dimension shape
 * the PM issues list uses, over the three dimensions signing data actually
 * has: status, the person who signed, and the creation date.
 */
export function SigningFilterControl({ signers, value, onChange }: SigningFilterControlProps) {
  const { t } = useLanguage();
  const f = t.signing.filter;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Dimension | null>(null);

  const dimensionLabel = (d: Dimension): string =>
    d === "status" ? f.status : d === "signer" ? f.signedBy : f.date;

  const statusLabel = (status: SigningItemStatus): string =>
    ({
      awaiting_you: t.signing.status.awaitingYou,
      awaiting_others: t.signing.status.awaitingOthers,
      finalizing: t.signing.status.finalizing,
      signed: t.signing.status.signed,
      failed: t.signing.status.failed,
      cancelled: t.signing.status.cancelled,
      expired: t.signing.status.expired,
      unknown: t.signing.status.unknown,
    })[status];

  const toggleStatus = (status: SigningItemStatus) =>
    onChange({
      ...value,
      statuses: value.statuses.includes(status)
        ? value.statuses.filter((s) => s !== status)
        : [...value.statuses, status],
    });

  const toggleSigner = (id: number) =>
    onChange({
      ...value,
      signerUserIds: value.signerUserIds.includes(id)
        ? value.signerUserIds.filter((s) => s !== id)
        : [...value.signerUserIds, id],
    });

  const openAt = (d: Dimension) => {
    setStep(d);
    setOpen(true);
  };

  const chips: Array<{ key: Dimension; label: string; onRemove: () => void }> = [];
  if (value.statuses.length > 0) {
    const summary =
      statusLabel(value.statuses[0]) +
      (value.statuses.length > 1 ? ` +${value.statuses.length - 1}` : "");
    chips.push({
      key: "status",
      label: `${f.status}: ${summary}`,
      onRemove: () => onChange({ ...value, statuses: [] }),
    });
  }
  if (value.signerUserIds.length > 0) {
    const first = signers.find((s) => s.id === value.signerUserIds[0]);
    const summary =
      (first?.name ?? String(value.signerUserIds[0])) +
      (value.signerUserIds.length > 1 ? ` +${value.signerUserIds.length - 1}` : "");
    chips.push({
      key: "signer",
      label: `${f.signedBy}: ${summary}`,
      onRemove: () => onChange({ ...value, signerUserIds: [] }),
    });
  }
  if (value.from || value.to) {
    chips.push({
      key: "date",
      label: `${f.date}: ${value.from ?? "…"} – ${value.to ?? "…"}`,
      onRemove: () => onChange({ ...value, from: null, to: null }),
    });
  }

  const stepHeader = (d: Dimension) => {
    const Icon = DIMENSION_ICON[d];
    return (
      <div className="flex items-center gap-1.5 border-b px-1 py-1">
        <button
          type="button"
          onClick={() => setStep(null)}
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={f.back}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium">{dimensionLabel(d)}</span>
      </div>
    );
  };

  const checkOption = (label: string, active: boolean, onClick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted"
    >
      <span className="truncate">{label}</span>
      {active && <Check className="h-3.5 w-3.5" />}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-2 pr-1 text-xs"
        >
          <button
            type="button"
            onClick={() => openAt(chip.key)}
            className="max-w-[200px] truncate hover:text-foreground"
          >
            {chip.label}
          </button>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`${f.clear} ${chip.label}`}
            className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      {chips.length > 0 && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_SIGNING_FILTER)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {f.clear}
          <X className="h-3 w-3" />
        </button>
      )}

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setStep(null);
        }}
        modal={true}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="control"
            className="gap-1.5"
            {...pageInfoTarget("signing.filter")}
          >
            <SlidersHorizontal className="opacity-60" />
            {f.label}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 p-0">
          {step === null && (
            <div className="p-1">
              {DIMENSIONS.map((d) => {
                const Icon = DIMENSION_ICON[d];
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setStep(d)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted"
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      {dimensionLabel(d)}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}

          {step === "status" && (
            <div className="p-1">
              {stepHeader("status")}
              <div className="pt-1">
                {STATUSES.map((status) =>
                  checkOption(
                    statusLabel(status),
                    value.statuses.includes(status),
                    () => toggleStatus(status),
                    status
                  )
                )}
              </div>
            </div>
          )}

          {step === "signer" && (
            <div className="p-1">
              {stepHeader("signer")}
              <div className="max-h-60 overflow-y-auto pt-1">
                {signers.map((signer) =>
                  checkOption(
                    signer.name || String(signer.id),
                    value.signerUserIds.includes(signer.id),
                    () => toggleSigner(signer.id),
                    String(signer.id)
                  )
                )}
              </div>
            </div>
          )}

          {step === "date" && (
            <div className="p-1">
              {stepHeader("date")}
              {/* Native date inputs — a nested DatePicker popover portals to
                  body, which the outer modal popover treats as an outside
                  pointer-down and dismisses on. */}
              <div className="flex flex-col gap-2 p-2">
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {f.from}
                  <Input
                    type="date"
                    className="h-7 px-2 text-xs"
                    value={value.from ?? ""}
                    onChange={(event) => onChange({ ...value, from: event.target.value || null })}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {f.to}
                  <Input
                    type="date"
                    className="h-7 px-2 text-xs"
                    value={value.to ?? ""}
                    onChange={(event) => onChange({ ...value, to: event.target.value || null })}
                  />
                </label>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
